import { GenericParser } from 'nuqs';
import { ComponentType } from 'react';
import { MessageDescriptor } from 'react-intl';

/**
 * Sort value passed to queryFn params. Matches a single item of MRT_SortingState.
 */
export interface ListViewSort {
  id: string;
  desc: boolean;
}

export type ListViewFilters = Record<string, unknown>;

export interface ListViewQueryParams {
  pageIndex: number;
  pageSize: number;
  sort?: ListViewSort | null;
  format?: 'csv' | 'xls' | null;
  filters?: ListViewFilters | null;
}

/**
 * Params passed to exportFn. No pagination (export uses current filters/sort only).
 */
export type ListViewExportParams = Pick<ListViewQueryParams, 'sort' | 'format' | 'filters'>;

/** Base props injected by the filter bar into every filter component. */
export interface FilterDefinitionBaseProps {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  label: any;
  /** Only set for addable filters; undefined for permanent filters. */
  isActive?: boolean;
  /** Only provided for addable (non-permanent) filters; used by RemovableFilterSlot. */
  onRemove?: () => void;
  // Parser is injected by FilterBar
  parser: GenericParser<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
}

/**
 * Filter definition that specifies which component to render and with what props.
 * This is a composition-based approach where the configuration explicitly defines
 * the component and its props, removing the need for type discriminators.
 *
 * @template Props - Component props type; must include FilterDefinitionBaseProps (injected by the bar).
 *                   Default is any so heterogeneous filter arrays (mixed component prop types) type-check.
 * @template Value - The type of the value parsed from the URL. Defaults to unknown.
 */
export interface FilterDefinition<
  Props extends FilterDefinitionBaseProps = any, // eslint-disable-line @typescript-eslint/no-explicit-any
  Value = unknown,
> {
  /** Unique identifier for the filter (used as URL query parameter key) */
  name: string;

  /** Message descriptor for the label (filter bar and "Add Filter" menu). Pass false to show no label in the bar. */
  label: MessageDescriptor | false;

  /** Whether this filter is always visible (permanent) or can be added/removed */
  isPermanent?: boolean;

  /**
   * When true, the filter's toApi output (or default $eq string) is sent as a single top-level
   * query param named `name` instead of inside the filter array.
   * When a function, receives the parsed filter value and optional context; its return object is
   * merged into top-level query params (omit keys with null/undefined/empty string values).
   * `toApi` is not used when `raw` is a function — put any mapping inside `raw`.
   * Not used together with the filter array for the same filter entry.
   */
  raw?:
    | boolean
    | ((
        value: Value,
        context?: Record<string, unknown>,
      ) => Record<string, string> | null | undefined);

  /** Parser for reading/writing this filter value from/to URL */
  parser: GenericParser<Value>;

  /** The React component to render for this filter */
  component: ComponentType<Props>;

  /** Props to pass to the component (name and label will be injected automatically from parent) */
  componentProps?: Partial<Omit<Props, keyof FilterDefinitionBaseProps>>;

  /**
   * Client-side filter function for {@link ListViewLocal}.
   * Returns true if the item matches the filter value.
   * Defaults to equality comparison on `item[filter.name]` if not provided.
   */
  filterFn?: (item: unknown, value: Value) => boolean;

  /**
   * Transforms the filter value into CRUD API filter string(s).
   * If provided, the listing adapter uses this instead of default "name||$eq||value".
   * Return a single string or array of strings (e.g. for date range $gte/$lte).
   * Return null (or undefined) to omit this filter from the API request.
   * Context (e.g. timezone) is passed when the adapter is called with options.
   */
  toApi?: (value: Value, context?: Record<string, unknown>) => string | string[] | null | undefined;
}

/**
 * Helper to create a typed filter definition. Infers Props and Value from the definition
 * so that toApi receives the correct value type (e.g. boolean, string) without manual casting.
 */
export function createFilter<P extends FilterDefinitionBaseProps, V>(
  def: FilterDefinition<P, V>,
): FilterDefinition<P, V> {
  return def;
}
